import React, { useState } from 'react';

// ── Pipedrive round-robin staff assignment ──────────────────────────────────
// TODO: Replace the id values below with your actual Pipedrive user IDs.
// Find them: Pipedrive → Settings → Manage Users  (or GET /api/v1/users)
const STAFF_USERS = [
  { id: 22094014, name: 'Aaron Townsend' },
  { id: 16336122, name: 'Joel Woodward' },
];

function getNextStaffId(): number {
  const last = parseInt(localStorage.getItem('rw_staff_idx') || '-1', 10);
  const next = (last + 1) % STAFF_USERS.length;
  localStorage.setItem('rw_staff_idx', String(next));
  return STAFF_USERS[next].id;
}

function getNextBusinessDay(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0]; // YYYY-MM-DD
}

// TODO: Create a "Retirement Wealth" label in Pipedrive → Leads → Lead Labels,
// then paste the UUID here so leads are automatically tagged.
const RW_LABEL_ID = '1640c8f0-1ee3-11f1-a308-6928be7fe0e5';
const PIPEDRIVE_API_KEY = 'e1539707d66c411b95cee99d607203941c792331';

type ModalStep = 'form1' | 'form2' | 'processing' | 'success';

const RetirementWealth: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('form1');
  const [mFirstName, setMFirstName] = useState('');
  const [mLastName, setMLastName] = useState('');
  const [mEmail, setMEmail] = useState('');
  const [mPhone, setMPhone] = useState('');
  const [mIncome, setMIncome] = useState('');
  const [mTimeline, setMTimeline] = useState('');
  const [mErrors, setMErrors] = useState<Record<string, string>>({});

  const openModal = () => {
    setModalStep('form1');
    setMFirstName(''); setMLastName(''); setMEmail(''); setMPhone('');
    setMIncome(''); setMTimeline(''); setMErrors({});
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = '';
  };

  const validateStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!mFirstName.trim()) errs.firstName = 'Required';
    if (!mLastName.trim()) errs.lastName = 'Required';
    if (!mEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mEmail.trim())) errs.email = 'Valid email required';
    if (!mPhone.trim() || mPhone.replace(/\D/g, '').length < 8) errs.phone = 'Valid phone required';
    setMErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!mIncome) errs.income = 'Please select an option';
    if (!mTimeline) errs.timeline = 'Please select an option';
    setMErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1Next = () => {
    if (validateStep1()) setModalStep('form2');
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setModalStep('processing');

    const staffUserId = getNextStaffId();
    const fullName = `${mFirstName.trim()} ${mLastName.trim()}`;
    const noteContent = `Source: Retirement Wealth Page\nName: ${fullName}\nEmail: ${mEmail.trim()}\nPhone: ${mPhone.trim()}\nHousehold Income: ${mIncome}\nRetirement Timeline: ${mTimeline}`;

    try {
      // STEP 1: Create Person
      const personRes = await fetch(
        `https://oneproperty.pipedrive.com/api/v1/persons?api_token=${PIPEDRIVE_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            name: fullName,
            email: [{ value: mEmail.trim(), primary: true, label: 'work' }],
            phone: [{ value: mPhone.trim(), primary: true, label: 'mobile' }],
            visible_to: '3',
          }),
        }
      );
      const personResult = await personRes.json();

      if (!personResult.success) {
        console.error('Failed to create person:', personResult.error);
        setModalStep('success');
        return;
      }

      const personId: number = personResult.data.id;

      // STEP 2: Create Lead
      const leadRes = await fetch(
        `https://oneproperty.pipedrive.com/api/v1/leads?api_token=${PIPEDRIVE_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            title: `${fullName} — Retirement Wealth Enquiry`,
            person_id: personId,
            label_ids: RW_LABEL_ID ? [RW_LABEL_ID] : [],
            value: { amount: 0, currency: 'AUD' },
          }),
        }
      );
      const leadResult = await leadRes.json();
      const leadId: string | null = leadResult.success ? leadResult.data?.id : null;

      // STEP 3: Create Activity — round-robin assigned strategy call
      await fetch(
        `https://oneproperty.pipedrive.com/api/v1/activities?api_token=${PIPEDRIVE_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            subject: `Strategy Session — ${fullName}`,
            type: 'call',
            due_date: getNextBusinessDay(),
            due_time: '09:00',
            duration: '00:30',
            user_id: staffUserId,
            person_id: personId,
            ...(leadId ? { lead_id: leadId } : {}),
            note: `Income: ${mIncome} | Retirement in: ${mTimeline} | Ph: ${mPhone.trim()}`,
            busy_flag: false,
            done: false,
          }),
        }
      );

      // STEP 4: Add Note to Lead
      if (leadId) {
        await fetch(
          `https://oneproperty.pipedrive.com/api/v1/notes?api_token=${PIPEDRIVE_API_KEY}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ content: noteContent, lead_id: leadId }),
          }
        );
      }
    } catch (err) {
      console.error('Pipedrive integration error:', err);
    }

    setModalStep('success');
  };

  return (
    <>
      <style>{`
        .rw *, .rw *::before, .rw *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .rw {
          font-family: 'Inter', sans-serif;
          background: #ffffff;
          color: #1a1a2e;
          line-height: 1.6;
        }

        /* ─── HERO ─────────────────────────────────────────── */
        .rw-hero {
          background: linear-gradient(135deg, #0a1628 0%, #0d1b3e 50%, #0f2652 100%);
          padding: 80px 48px 88px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .rw-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(ellipse at 75% 40%, rgba(0,196,180,0.09) 0%, transparent 55%),
            radial-gradient(ellipse at 20% 80%, rgba(59,130,246,0.06) 0%, transparent 50%);
        }
        .rw-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(0,196,180,0.12);
          border: 1px solid rgba(0,196,180,0.28);
          color: #00c4b4;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 8px 20px;
          border-radius: 100px;
          margin-bottom: 36px;
          position: relative;
        }
        .rw-hero h1 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2.2rem, 5.5vw, 3.8rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.1;
          margin-bottom: 24px;
          position: relative;
        }
        .rw-hero h1 em {
          font-style: normal;
          color: #00c4b4;
        }
        .rw-hero-sub {
          font-size: clamp(1rem, 2vw, 1.2rem);
          color: rgba(255,255,255,0.78);
          max-width: 680px;
          margin: 0 auto 36px;
          position: relative;
          line-height: 1.75;
        }
        .rw-hero-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
          flex-wrap: wrap;
          position: relative;
        }
        .rw-hero-trust-item {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 0.82rem;
          color: rgba(255,255,255,0.5);
        }
        .rw-hero-trust-item svg { color: #00c4b4; flex-shrink: 0; }

        /* ─── VIDEO ─────────────────────────────────────────── */
        .rw-video-section {
          padding: 72px 24px 64px;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: #ffffff;
        }
        .rw-video-hook {
          max-width: 700px;
          text-align: center;
          margin-bottom: 40px;
        }
        .rw-video-hook h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.5rem, 3.5vw, 2.2rem);
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 14px;
          line-height: 1.25;
        }
        .rw-video-hook p {
          font-size: 1.02rem;
          color: #6b7280;
          line-height: 1.75;
        }
        .rw-video-wrap {
          width: 100%;
          max-width: 840px;
          aspect-ratio: 16/9;
          background: #1a1a2e;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(13,27,62,0.28);
          position: relative;
        }
        .rw-video-cta {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          margin-top: 44px;
        }

        /* ─── BUTTONS ────────────────────────────────────────── */
        .rw-btn {
          display: inline-block;
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          text-decoration: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.22s ease;
          border: 2px solid transparent;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .rw-btn-primary {
          padding: 18px 56px;
          font-size: 1.05rem;
          background: #00c4b4;
          color: #0d1b3e;
          border-color: #00c4b4;
        }
        .rw-btn-primary:hover {
          background: #00dccb;
          border-color: #00dccb;
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(0,196,180,0.38);
        }
        .rw-btn-outline {
          padding: 16px 44px;
          font-size: 0.95rem;
          background: transparent;
          color: #0d1b3e;
          border-color: rgba(13,27,62,0.3);
        }
        .rw-btn-outline:hover { background: #0d1b3e; color: #ffffff; border-color: #0d1b3e; }
        .rw-btn-nudge {
          font-size: 0.82rem;
          color: #9ca3af;
          text-align: center;
        }

        /* ─── LOGO STRIP ─────────────────────────────────────── */
        .rw-logo-strip {
          padding: 22px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-top: 1px solid #e5e9f2;
          border-bottom: 1px solid #e5e9f2;
          background: #fafbff;
        }
        .rw-brand { display: flex; align-items: center; gap: 14px; }
        .rw-brand-name {
          font-family: 'Manrope', sans-serif;
          font-size: 1.35rem;
          font-weight: 800;
          color: #0d1b3e;
        }

        /* ─── BIG NUMBERS ────────────────────────────────────── */
        .rw-numbers {
          background: linear-gradient(135deg, #0a1628 0%, #0d1b3e 60%, #0f2652 100%);
          padding: 80px 24px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .rw-numbers::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 25% 50%, rgba(0,196,180,0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.05) 0%, transparent 45%);
        }
        .rw-numbers-eyebrow {
          display: inline-block;
          background: rgba(0,196,180,0.12);
          border: 1px solid rgba(0,196,180,0.28);
          color: #00c4b4;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 7px 18px;
          border-radius: 100px;
          margin-bottom: 24px;
          position: relative;
        }
        .rw-numbers h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.7rem, 3.8vw, 2.6rem);
          font-weight: 800;
          color: #ffffff;
          line-height: 1.25;
          max-width: 700px;
          margin: 0 auto 56px;
          position: relative;
        }
        .rw-numbers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1px;
          max-width: 880px;
          margin: 0 auto;
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          overflow: hidden;
          position: relative;
        }
        .rw-num-cell {
          background: rgba(255,255,255,0.03);
          padding: 44px 28px;
          text-align: center;
          backdrop-filter: blur(6px);
        }
        .rw-num-cell:hover { background: rgba(255,255,255,0.06); }
        .rw-num-stat {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(2.4rem, 5vw, 3.8rem);
          font-weight: 800;
          color: #00c4b4;
          line-height: 1;
          margin-bottom: 12px;
        }
        .rw-num-strong {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.8);
          display: block;
          margin-bottom: 6px;
        }
        .rw-num-label {
          font-size: 0.88rem;
          color: rgba(255,255,255,0.5);
          line-height: 1.55;
        }

        /* ─── CASE STUDY ─────────────────────────────────────── */
        .rw-case { padding: 88px 24px; background: #ffffff; }
        .rw-case-header {
          text-align: center;
          max-width: 720px;
          margin: 0 auto 56px;
        }
        .rw-case-eyebrow {
          display: inline-block;
          background: rgba(0,196,180,0.15);
          color: #00c4b4;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 7px 18px;
          border-radius: 100px;
          margin-bottom: 20px;
        }
        .rw-case-header h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 800;
          color: #1a1a2e;
          line-height: 1.2;
          margin-bottom: 18px;
        }
        .rw-case-header p {
          font-size: 1.05rem;
          color: #6b7280;
          line-height: 1.75;
        }
        .rw-case-card {
          max-width: 920px;
          margin: 0 auto;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 8px 56px rgba(0,0,0,0.1);
          border: 1px solid #e5e9f2;
        }
        .rw-case-card-top {
          background: #0d1b3e;
          padding: 28px 36px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .rw-case-address-group { display: flex; align-items: center; gap: 14px; }
        .rw-case-pin {
          width: 42px; height: 42px;
          background: rgba(0,196,180,0.15);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .rw-case-address {
          font-family: 'Manrope', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: #ffffff;
        }
        .rw-case-subtype {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.45);
          margin-top: 3px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .rw-case-status-badge {
          background: rgba(0,196,180,0.12);
          border: 1px solid rgba(0,196,180,0.3);
          color: #00c4b4;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 7px 18px;
          border-radius: 100px;
          white-space: nowrap;
        }
        .rw-case-facts {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-bottom: 1px solid #f0f2f8;
        }
        .rw-case-fact {
          padding: 26px 20px;
          border-right: 1px solid #f0f2f8;
          text-align: center;
        }
        .rw-case-fact:last-child { border-right: none; }
        .rw-case-fact-label {
          font-size: 0.64rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .rw-case-fact-value {
          font-family: 'Manrope', sans-serif;
          font-size: 1.2rem;
          font-weight: 800;
          color: #1a1a2e;
        }
        .rw-case-fact-value.teal { color: #009e90; }
        .rw-case-results {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .rw-case-result {
          padding: 36px 30px;
          border-right: 1px solid #f0f2f8;
        }
        .rw-case-result:last-child { border-right: none; }
        .rw-case-result-icon {
          width: 38px; height: 38px;
          border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 14px;
        }
        .rw-case-result-tag {
          font-size: 0.64rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .rw-case-result-stat {
          font-family: 'Manrope', sans-serif;
          font-size: 2rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 12px;
        }
        .rw-case-result-desc {
          font-size: 0.875rem;
          color: #6b7280;
          line-height: 1.65;
        }
        .rw-case-result-desc strong { color: #1a1a2e; }

        /* ─── WHY ────────────────────────────────────────────── */
        .rw-why { background: #f4f6fb; padding: 88px 24px; }
        .rw-why-header {
          text-align: center;
          max-width: 660px;
          margin: 0 auto 56px;
        }
        .rw-why-header h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.8rem, 4vw, 2.7rem);
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .rw-why-header p { font-size: 1rem; color: #6b7280; line-height: 1.75; }
        .rw-why-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          max-width: 1120px;
          margin: 0 auto;
        }
        .rw-why-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 32px 24px;
          box-shadow: 0 2px 20px rgba(0,0,0,0.05);
          border: 1px solid #eef0f7;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .rw-why-card:hover { transform: translateY(-3px); box-shadow: 0 8px 32px rgba(0,0,0,0.09); }
        .rw-why-card-icon {
          width: 46px; height: 46px;
          border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 18px;
        }
        .rw-why-card h4 { font-size: 1rem; font-weight: 700; color: #1a1a2e; margin-bottom: 10px; line-height: 1.3; }
        .rw-why-card p { font-size: 0.875rem; color: #6b7280; line-height: 1.7; }

        /* ─── ROADMAP ────────────────────────────────────────── */
        .rw-roadmap { background: #ffffff; padding: 88px 24px; }
        .rw-roadmap-header {
          text-align: center;
          max-width: 640px;
          margin: 0 auto 56px;
        }
        .rw-roadmap-header h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.8rem, 4vw, 2.7rem);
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 14px;
          line-height: 1.2;
        }
        .rw-roadmap-header p { font-size: 1rem; color: #6b7280; line-height: 1.75; }
        .rw-roadmap-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          max-width: 1080px;
          margin: 0 auto;
        }
        .rw-fig-card {
          background: #f9fafc;
          border: 1px solid #e5e9f2;
          border-radius: 20px;
          padding: 36px 32px;
        }
        .rw-fig-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; }
        .rw-fig-card h3 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.15rem;
          font-weight: 800;
          color: #1a1a2e;
        }
        .rw-fig-card-timeline { font-size: 0.78rem; color: #9ca3af; margin-top: 4px; }
        .rw-fig-icon {
          width: 42px; height: 42px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .rw-phase { margin-bottom: 22px; }
        .rw-phase-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 9px; }
        .rw-phase-name { font-size: 0.9rem; font-weight: 700; color: #1a1a2e; }
        .rw-badge { font-size: 0.64rem; font-weight: 700; letter-spacing: 0.08em; padding: 4px 10px; border-radius: 5px; text-transform: uppercase; }
        .rw-badge.complete { background: rgba(0,196,180,0.15); color: #00c4b4; }
        .rw-badge.in-progress { background: rgba(13,27,62,0.08); color: #0d1b3e; }
        .rw-badge.upcoming { background: #f3f4f6; color: #9ca3af; }
        .rw-progress-bar { height: 6px; background: #e5e9f2; border-radius: 100px; overflow: hidden; margin-bottom: 7px; }
        .rw-progress-fill { height: 100%; border-radius: 100px; }
        .rw-progress-fill.green { background: #00c4b4; width: 100%; }
        .rw-progress-fill.blue { background: #0d1b3e; width: 55%; }
        .rw-progress-fill.grey { background: #d1d5db; width: 6%; }
        .rw-phase-desc { font-size: 0.8rem; color: #9ca3af; line-height: 1.5; }
        .rw-fig-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 24px;
          border-top: 1px solid #e5e9f2;
          margin-top: 12px;
        }
        .rw-fig-footer .label { font-size: 0.9rem; font-weight: 600; color: #6b7280; }
        .rw-fig-footer .value {
          font-family: 'Manrope', sans-serif;
          font-size: 1.8rem;
          font-weight: 800;
          color: #0d1b3e;
        }

        /* ─── FOOTER CTA ─────────────────────────────────────── */
        .rw-footer-cta {
          background: linear-gradient(135deg, #0a1628 0%, #0d1b3e 55%, #0a1628 100%);
          padding: 104px 24px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .rw-footer-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 50% 110%, rgba(0,196,180,0.13) 0%, transparent 55%),
            radial-gradient(ellipse at 50% -20%, rgba(59,130,246,0.05) 0%, transparent 50%);
        }
        .rw-footer-cta-inner {
          position: relative;
          max-width: 640px;
          margin: 0 auto;
        }
        .rw-footer-cta h2 {
          font-family: 'Manrope', sans-serif;
          font-size: clamp(1.9rem, 4.5vw, 3.2rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 18px;
          line-height: 1.15;
        }
        .rw-footer-cta h2 em { font-style: normal; color: #00c4b4; }
        .rw-footer-cta p {
          color: rgba(255,255,255,0.58);
          margin-bottom: 44px;
          font-size: 1.05rem;
          line-height: 1.75;
        }
        .rw-footer-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
          margin-top: 24px;
        }
        .rw-footer-trust-item {
          display: flex; align-items: center; gap: 6px;
          font-size: 0.8rem; color: rgba(255,255,255,0.4);
        }
        .rw-footer-trust-item svg { color: rgba(0,196,180,0.65); }

        /* ─── DIVIDER ────────────────────────────────────────── */
        .rw-divider {
          border: none;
          height: 1px;
          background: linear-gradient(to right, transparent, #d1d9f0, transparent);
          margin: 0;
        }

        /* ─── MODAL ──────────────────────────────────────────── */
        .rw-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(10,22,40,0.82);
          backdrop-filter: blur(6px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .rw-modal {
          background: #ffffff;
          border-radius: 20px;
          padding: 48px 44px;
          width: 100%;
          max-width: 520px;
          position: relative;
          box-shadow: 0 32px 80px rgba(0,0,0,0.35);
          animation: rwModalIn 0.26s cubic-bezier(0.34,1.56,0.64,1);
          max-height: 90vh;
          overflow-y: auto;
        }
        @keyframes rwModalIn {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        .rw-modal-close {
          position: absolute;
          top: 16px; right: 16px;
          background: #f4f6fb;
          border: none;
          border-radius: 8px;
          width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: #6b7280;
          font-size: 16px;
          line-height: 1;
          transition: background 0.15s;
          font-family: 'Inter', sans-serif;
        }
        .rw-modal-close:hover { background: #e5e9f2; color: #1a1a2e; }
        .rw-modal-eyebrow {
          display: inline-block;
          background: rgba(0,196,180,0.12);
          border: 1px solid rgba(0,196,180,0.28);
          color: #00c4b4;
          font-size: 0.67rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 100px;
          margin-bottom: 16px;
        }
        .rw-modal h3 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          color: #1a1a2e;
          line-height: 1.2;
          margin-bottom: 8px;
        }
        .rw-modal-sub {
          font-size: 0.88rem;
          color: #6b7280;
          margin-bottom: 26px;
          line-height: 1.65;
        }
        .rw-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 14px;
        }
        .rw-form-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 14px;
        }
        .rw-form-field label {
          font-size: 0.76rem;
          font-weight: 600;
          color: #374151;
          letter-spacing: 0.03em;
        }
        .rw-form-field input,
        .rw-form-field select {
          padding: 12px 14px;
          font-size: 0.95rem;
          border: 1.5px solid #d1d5db;
          border-radius: 8px;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s;
          font-family: 'Inter', sans-serif;
          color: #1a1a2e;
          background: #fff;
          -webkit-appearance: none;
          appearance: none;
        }
        .rw-form-field input:focus,
        .rw-form-field select:focus {
          border-color: #00c4b4;
          box-shadow: 0 0 0 3px rgba(0,196,180,0.12);
        }
        .rw-form-field input.error,
        .rw-form-field select.error {
          border-color: #ef4444;
        }
        .rw-form-error {
          font-size: 0.75rem;
          color: #ef4444;
          margin-top: 2px;
        }
        .rw-modal-submit {
          width: 100%;
          padding: 15px;
          font-size: 1rem;
          font-weight: 700;
          font-family: 'Inter', sans-serif;
          background: #00c4b4;
          color: #0d1b3e;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 6px;
          letter-spacing: 0.02em;
        }
        .rw-modal-submit:hover {
          background: #00dccb;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(0,196,180,0.35);
        }
        .rw-modal-trust {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 18px;
          flex-wrap: wrap;
          margin-top: 16px;
        }
        .rw-modal-trust-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.73rem;
          color: #9ca3af;
        }
        .rw-modal-trust-item svg { color: #00c4b4; }
        .rw-modal-back {
          background: none;
          border: none;
          color: #9ca3af;
          font-size: 0.82rem;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          padding: 0;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: color 0.15s;
        }
        .rw-modal-back:hover { color: #1a1a2e; }
        /* Processing */
        .rw-modal-processing {
          text-align: center;
          padding: 24px 0;
        }
        .rw-spin {
          width: 52px; height: 52px;
          border: 3px solid #e5e9f2;
          border-top-color: #00c4b4;
          border-radius: 50%;
          animation: rwSpin 0.75s linear infinite;
          margin: 0 auto 24px;
        }
        @keyframes rwSpin { to { transform: rotate(360deg); } }
        .rw-modal-processing h4 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.3rem;
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 8px;
        }
        .rw-modal-processing p { font-size: 0.88rem; color: #6b7280; }
        /* Success */
        .rw-modal-success { text-align: center; padding: 12px 0; }
        .rw-modal-success-icon {
          width: 64px; height: 64px;
          background: rgba(0,196,180,0.12);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 22px;
        }
        .rw-modal-success h4 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 800;
          color: #1a1a2e;
          margin-bottom: 10px;
        }
        .rw-modal-success > p {
          font-size: 0.93rem;
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 24px;
        }
        .rw-modal-success-steps {
          background: #f4f6fb;
          border-radius: 12px;
          padding: 20px 22px;
          text-align: left;
          margin-bottom: 20px;
        }
        .rw-modal-success-step {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
        }
        .rw-modal-success-step:last-child { margin-bottom: 0; }
        .rw-modal-success-step-num {
          width: 24px; height: 24px;
          background: #0d1b3e;
          color: #fff;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.7rem;
          font-weight: 800;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .rw-modal-success-step-text {
          font-size: 0.855rem;
          color: #374151;
          line-height: 1.55;
        }
        .rw-modal-success-close {
          width: 100%;
          padding: 14px;
          font-size: 0.95rem;
          font-weight: 700;
          font-family: 'Inter', sans-serif;
          background: #0d1b3e;
          color: #fff;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.18s;
        }
        .rw-modal-success-close:hover { background: #0f2652; }

        /* ─── RESPONSIVE ─────────────────────────────────────── */
        @media (max-width: 960px) {
          .rw-case-facts { grid-template-columns: repeat(2, 1fr); }
          .rw-case-fact:nth-child(2) { border-right: none; }
          .rw-case-fact:nth-child(1),
          .rw-case-fact:nth-child(2) { border-bottom: 1px solid #f0f2f8; }
          .rw-roadmap-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .rw-hero { padding: 60px 24px 72px; }
          .rw-numbers-grid { grid-template-columns: 1fr; }
          .rw-why-grid { grid-template-columns: 1fr 1fr; }
          .rw-case-results { grid-template-columns: 1fr; }
          .rw-case-result { border-right: none; border-bottom: 1px solid #f0f2f8; }
          .rw-case-result:last-child { border-bottom: none; }
          .rw-case-card-top { flex-direction: column; align-items: flex-start; }
        }
        @media (max-width: 560px) {
          .rw-modal { padding: 36px 22px; border-radius: 16px; }
          .rw-form-row { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .rw-why-grid { grid-template-columns: 1fr; }
          .rw-case-facts { grid-template-columns: 1fr 1fr; }
          .rw-btn-primary { padding: 17px 36px; font-size: 1rem; }
          .rw-hero-trust { gap: 16px; }
        }
      `}</style>

      {/* ── ENQUIRY MODAL ───────────────────────────────────── */}
      {showModal && (
        <div
          className="rw-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="rw-modal">
            <button className="rw-modal-close" onClick={closeModal} aria-label="Close">✕</button>

            {/* STEP 1 — Contact Details */}
            {modalStep === 'form1' && (
              <>
                <div className="rw-modal-eyebrow">Free Strategy Session</div>
                <h3>Let's Map Your Retirement Strategy</h3>
                <p className="rw-modal-sub">
                  Tell us how to reach you and a specialist will be in touch within one business day.
                </p>
                <div className="rw-form-row">
                  <div className="rw-form-field">
                    <label>First Name</label>
                    <input
                      type="text"
                      placeholder="Jane"
                      value={mFirstName}
                      onChange={(e) => setMFirstName(e.target.value)}
                      className={mErrors.firstName ? 'error' : ''}
                    />
                    {mErrors.firstName && <span className="rw-form-error">{mErrors.firstName}</span>}
                  </div>
                  <div className="rw-form-field">
                    <label>Last Name</label>
                    <input
                      type="text"
                      placeholder="Smith"
                      value={mLastName}
                      onChange={(e) => setMLastName(e.target.value)}
                      className={mErrors.lastName ? 'error' : ''}
                    />
                    {mErrors.lastName && <span className="rw-form-error">{mErrors.lastName}</span>}
                  </div>
                </div>
                <div className="rw-form-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="jane@example.com"
                    value={mEmail}
                    onChange={(e) => setMEmail(e.target.value)}
                    className={mErrors.email ? 'error' : ''}
                  />
                  {mErrors.email && <span className="rw-form-error">{mErrors.email}</span>}
                </div>
                <div className="rw-form-field">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    placeholder="04XX XXX XXX"
                    value={mPhone}
                    onChange={(e) => setMPhone(e.target.value)}
                    className={mErrors.phone ? 'error' : ''}
                  />
                  {mErrors.phone && <span className="rw-form-error">{mErrors.phone}</span>}
                </div>
                <button className="rw-modal-submit" onClick={handleStep1Next}>
                  Continue →
                </button>
                <div className="rw-modal-trust">
                  <span className="rw-modal-trust-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    No obligation
                  </span>
                  <span className="rw-modal-trust-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Privacy protected
                  </span>
                  <span className="rw-modal-trust-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    30-minute call
                  </span>
                </div>
              </>
            )}

            {/* STEP 2 — Qualifying Questions */}
            {modalStep === 'form2' && (
              <>
                <button className="rw-modal-back" onClick={() => setModalStep('form1')}>
                  ← Back
                </button>
                <div className="rw-modal-eyebrow">Almost There</div>
                <h3>A Couple of Quick Questions</h3>
                <p className="rw-modal-sub">
                  This helps us prepare a session that's tailored to your income and goals — not a generic pitch.
                </p>
                <div className="rw-form-field">
                  <label>Household Annual Income</label>
                  <select
                    value={mIncome}
                    onChange={(e) => setMIncome(e.target.value)}
                    className={mErrors.income ? 'error' : ''}
                  >
                    <option value="">Select your income range</option>
                    <option value="$150k – $200k">$150k – $200k</option>
                    <option value="$200k – $300k">$200k – $300k</option>
                    <option value="$300k – $500k">$300k – $500k</option>
                    <option value="$500k+">$500k+</option>
                  </select>
                  {mErrors.income && <span className="rw-form-error">{mErrors.income}</span>}
                </div>
                <div className="rw-form-field">
                  <label>Target Retirement Timeline</label>
                  <select
                    value={mTimeline}
                    onChange={(e) => setMTimeline(e.target.value)}
                    className={mErrors.timeline ? 'error' : ''}
                  >
                    <option value="">Select your timeline</option>
                    <option value="Within 5 years">Within 5 years</option>
                    <option value="5 – 10 years">5 – 10 years</option>
                    <option value="10 – 15 years">10 – 15 years</option>
                    <option value="15 – 20 years">15 – 20 years</option>
                    <option value="20+ years">20+ years</option>
                  </select>
                  {mErrors.timeline && <span className="rw-form-error">{mErrors.timeline}</span>}
                </div>
                <button className="rw-modal-submit" onClick={handleSubmit}>
                  Book My Free Strategy Session →
                </button>
                <div className="rw-modal-trust">
                  <span className="rw-modal-trust-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Tailored to your income
                  </span>
                  <span className="rw-modal-trust-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    No sales pitch
                  </span>
                </div>
              </>
            )}

            {/* PROCESSING */}
            {modalStep === 'processing' && (
              <div className="rw-modal-processing">
                <div className="rw-spin" />
                <h4>Reserving Your Spot…</h4>
                <p>We're adding your strategy session to our calendar now.</p>
              </div>
            )}

            {/* SUCCESS */}
            {modalStep === 'success' && (
              <div className="rw-modal-success">
                <div className="rw-modal-success-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h4>You're Booked In</h4>
                <p>
                  One of our specialists will call you within one business day to confirm your session time
                  and start mapping your retirement property strategy.
                </p>
                <div className="rw-modal-success-steps">
                  <div className="rw-modal-success-step">
                    <div className="rw-modal-success-step-num">1</div>
                    <div className="rw-modal-success-step-text">
                      <strong>Specialist assigned</strong> — your enquiry has been routed to a specialist and added to their calendar.
                    </div>
                  </div>
                  <div className="rw-modal-success-step">
                    <div className="rw-modal-success-step-num">2</div>
                    <div className="rw-modal-success-step-text">
                      <strong>Confirmation call</strong> — they'll reach out to confirm a time that suits you.
                    </div>
                  </div>
                  <div className="rw-modal-success-step">
                    <div className="rw-modal-success-step-num">3</div>
                    <div className="rw-modal-success-step-text">
                      <strong>Your 30-minute session</strong> — a clear, numbers-backed retirement property plan, built around your income.
                    </div>
                  </div>
                </div>
                <button className="rw-modal-success-close" onClick={closeModal}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="rw">

        {/* ── HERO ──────────────────────────────────────────── */}
        <section className="rw-hero">
          <div className="rw-hero-eyebrow">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Property Investment · For High-Income Australians
          </div>
          <h1>
            Most Australians Retire<br />on Less Than Half Their Income.<br />
            <em>You Don't Have To.</em>
          </h1>
          <p className="rw-hero-sub">
            If you're earning $200k+ and relying on super alone, you're leaving your retirement to chance.
            Discover how smart investors are using property to build real, lasting wealth — with full tax efficiency.
          </p>
          <div className="rw-hero-trust">
            <div className="rw-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Data-driven property selection
            </div>
            <div className="rw-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Legal tax minimisation strategies
            </div>
            <div className="rw-hero-trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Built for high-income earners
            </div>
          </div>
        </section>

        {/* ── VIDEO + CTA ─────────────────────────────────── */}
        <section className="rw-video-section">
          <div className="rw-video-hook">
            <h2>Watch: The Strategy Replacing Super<br />for High-Income Australians</h2>
            <p>
              See exactly how our clients legally reduce their tax bill while building a property portfolio
              engineered to fund the retirement they've earned — not a compromise of it.
            </p>
          </div>
          <div className="rw-video-wrap">
            {/*
              TO UPGRADE: Replace <video> with a Vimeo/YouTube iframe.
              Vimeo: <iframe src="https://player.vimeo.com/video/YOUR_ID?autoplay=0&title=0&byline=0&portrait=0"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none', borderRadius:18 }}
                allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
              YouTube: <iframe src="https://www.youtube.com/embed/YOUR_ID"
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none', borderRadius:18 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            */}
            <video
              controls
              playsInline
              preload="metadata"
              style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            >
              <source src="/One Property Australia Intro - Retirement Wealth.mp4" type="video/mp4" />
              Your browser does not support HTML5 video.
            </video>
          </div>
          <div className="rw-video-cta">
            <button className="rw-btn rw-btn-primary" onClick={openModal}>
              Book My Free Strategy Session
            </button>
            <span className="rw-btn-nudge">No obligation · 30-minute call with a specialist · Tailored to your income</span>
          </div>
        </section>

        {/* ── LOGO STRIP ──────────────────────────────────── */}
        <LogoStrip />
        <hr className="rw-divider" />

        {/* ── BIG NUMBERS ─────────────────────────────────── */}
        <section className="rw-numbers">
          <div className="rw-numbers-eyebrow">Real Client · Real Property · Real Numbers</div>
          <h2>
            What one strategic property purchase<br />delivered in under 8 months
          </h2>
          <div className="rw-numbers-grid">
            <div className="rw-num-cell">
              <div className="rw-num-stat">$83K</div>
              <span className="rw-num-strong">Capital Growth</span>
              <div className="rw-num-label">Unrealised gain in under 8 months</div>
            </div>
            <div className="rw-num-cell">
              <div className="rw-num-stat">4.62%</div>
              <span className="rw-num-strong">Gross Rental Yield</span>
              <div className="rw-num-label">$750/wk · $39,000 gross per year</div>
            </div>
            <div className="rw-num-cell">
              <div className="rw-num-stat">9.9%</div>
              <span className="rw-num-strong">Total Return</span>
              <div className="rw-num-label">Combined yield + capital growth in year one</div>
            </div>
          </div>
        </section>

        {/* ── CASE STUDY ──────────────────────────────────── */}
        <section className="rw-case">
          <div className="rw-case-header">
            <div className="rw-case-eyebrow">Live Case Study</div>
            <h2>One Property. One Decision.<br />$83,000 Richer in 8 Months.</h2>
            <p>
              This isn't a projection or a modelled scenario. This is what happened when one of our clients
              followed a data-led acquisition strategy in a carefully identified high-growth corridor —
              before the market caught on.
            </p>
          </div>

          <div className="rw-case-card">
            {/* Header */}
            <div className="rw-case-card-top">
              <div className="rw-case-address-group">
                <div className="rw-case-pin">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <div>
                  <div className="rw-case-address">LOT 6/2 Sunlight Drive, Imbil QLD 4570</div>
                  <div className="rw-case-subtype">Residential Investment · Queensland</div>
                </div>
              </div>
              <div className="rw-case-status-badge">Active Investment</div>
            </div>

            {/* 4 key facts */}
            <div className="rw-case-facts">
              <div className="rw-case-fact">
                <div className="rw-case-fact-label">Purchase Price</div>
                <div className="rw-case-fact-value">$842,000</div>
              </div>
              <div className="rw-case-fact">
                <div className="rw-case-fact-label">Date Purchased</div>
                <div className="rw-case-fact-value">May 2025</div>
              </div>
              <div className="rw-case-fact">
                <div className="rw-case-fact-label">Current Value</div>
                <div className="rw-case-fact-value teal">$925,000</div>
              </div>
              <div className="rw-case-fact">
                <div className="rw-case-fact-label">Weekly Rent</div>
                <div className="rw-case-fact-value teal">$750 / week</div>
              </div>
            </div>

            {/* 3 result columns */}
            <div className="rw-case-results">
              <div className="rw-case-result">
                <div className="rw-case-result-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2.5">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                  </svg>
                </div>
                <div className="rw-case-result-tag" style={{ color: '#00c4b4' }}>Capital Growth</div>
                <div className="rw-case-result-stat" style={{ color: '#009e90' }}>+$83,000</div>
                <div className="rw-case-result-desc">
                  This corridor was identified through data analysis — <strong>before the market moved.</strong>{' '}
                  That's the difference between chasing yesterday's headlines and acting on tomorrow's evidence.
                </div>
              </div>
              <div className="rw-case-result">
                <div className="rw-case-result-icon" style={{ background: 'rgba(13,27,62,0.07)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0d1b3e" strokeWidth="2.5">
                    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <div className="rw-case-result-tag" style={{ color: '#0d1b3e' }}>Rental Yield</div>
                <div className="rw-case-result-stat" style={{ color: '#0d1b3e' }}>$39K/yr</div>
                <div className="rw-case-result-desc">
                  <strong>$39,000 gross rent per year</strong> keeps holding costs manageable — so the
                  asset compounds in the background without demanding attention or cash flow.
                </div>
              </div>
              <div className="rw-case-result">
                <div className="rw-case-result-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2.5">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div className="rw-case-result-tag" style={{ color: '#00c4b4' }}>Total Return</div>
                <div className="rw-case-result-stat" style={{ color: '#009e90' }}>9.9%</div>
                <div className="rw-case-result-desc">
                  Combined yield and capital growth — in <strong>less than one year.</strong> The asset
                  is still in their portfolio, still working, still compounding toward retirement.
                </div>
              </div>
            </div>
          </div>

          {/* CTA — peak conviction moment */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '56px 24px 8px' }}>
            <button className="rw-btn rw-btn-primary" onClick={openModal}>
              See If This Strategy Works For You
            </button>
            <span className="rw-btn-nudge">Free strategy call — we'll map your numbers, not a stranger's</span>
          </div>
        </section>

        <hr className="rw-divider" />

        {/* ── WHY US ──────────────────────────────────────── */}
        <WhySection />

        <hr className="rw-divider" />

        {/* ── ROADMAP ─────────────────────────────────────── */}
        <section className="rw-roadmap">
          <div className="rw-roadmap-header">
            <h2>Your Property Wealth Roadmap</h2>
            <p>
              Whether you're starting with one property or building a full portfolio —
              here's what the journey looks like, and where it leads.
            </p>
          </div>
          <div className="rw-roadmap-grid">

            {/* First Investment Property */}
            <div className="rw-fig-card">
              <div className="rw-fig-card-header">
                <div>
                  <h3>First Investment Property</h3>
                  <div className="rw-fig-card-timeline">3–5 Year Timeline</div>
                </div>
                <div className="rw-fig-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Year 1: Foundation</span>
                  <span className="rw-badge complete">Complete</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill green"></div></div>
                <div className="rw-phase-desc">Build deposit, strengthen credit profile, identify target markets</div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Year 2–3: Acquisition</span>
                  <span className="rw-badge in-progress">In Progress</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill blue"></div></div>
                <div className="rw-phase-desc">Pre-approval, shortlist, purchase and settlement</div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Year 4–5: Optimisation</span>
                  <span className="rw-badge upcoming">Upcoming</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill grey"></div></div>
                <div className="rw-phase-desc">Tenant management, value-add improvements, refinance</div>
              </div>
              <div className="rw-fig-footer">
                <span className="label">Projected Value</span>
                <span className="value">$650K+</span>
              </div>
            </div>

            {/* Multi-Property Portfolio */}
            <div className="rw-fig-card">
              <div className="rw-fig-card-header">
                <div>
                  <h3>Multi-Property Portfolio</h3>
                  <div className="rw-fig-card-timeline">10+ Year Timeline</div>
                </div>
                <div className="rw-fig-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Years 1–3: First Property</span>
                  <span className="rw-badge complete">Complete</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill green"></div></div>
                <div className="rw-phase-desc">Establish your foundation investment property</div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Years 4–7: Portfolio Growth</span>
                  <span className="rw-badge in-progress">In Progress</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill blue"></div></div>
                <div className="rw-phase-desc">Add 2–3 properties using equity from your first</div>
              </div>
              <div className="rw-phase">
                <div className="rw-phase-top">
                  <span className="rw-phase-name">Years 8–10+: Wealth Creation</span>
                  <span className="rw-badge upcoming">Upcoming</span>
                </div>
                <div className="rw-progress-bar"><div className="rw-progress-fill grey"></div></div>
                <div className="rw-phase-desc">Scale to 5+ properties, activate passive income streams</div>
              </div>
              <div className="rw-fig-footer">
                <span className="label">Projected Portfolio Value</span>
                <span className="value">$3.2M+</span>
              </div>
            </div>

          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '56px 24px 8px' }}>
            <button className="rw-btn rw-btn-primary" onClick={openModal}>
              Start Building My Roadmap
            </button>
            <span className="rw-btn-nudge">Free, no-obligation session with a specialist</span>
          </div>
        </section>

        {/* ── FOOTER CTA ──────────────────────────────────── */}
        <section className="rw-footer-cta" id="contact">
          <div className="rw-footer-cta-inner">
            <h2>
              Ready to Build the Retirement<br />
              <em>Super Can't Promise?</em>
            </h2>
            <p>
              Speak with a specialist who works exclusively with high-income Australians.
              No sales pitch — just a clear, honest picture of what's possible for your situation.
            </p>
            <button className="rw-btn rw-btn-primary" onClick={openModal}>
              Book My Free Consultation
            </button>
            <div className="rw-footer-trust">
              <div className="rw-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                No obligation
              </div>
              <div className="rw-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                30-minute session
              </div>
              <div className="rw-footer-trust-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Tailored to your income &amp; goals
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

/* ── Sub-components ─────────────────────────────────── */

const LogoStrip: React.FC = () => (
  <div className="rw-logo-strip">
    <div className="rw-brand">
      <img src="/6163874b34ebcf3588aeb61e_one-logo.svg" alt="One Property Australia" style={{ height: 44, width: 'auto' }} />
      <div>
        <div className="rw-brand-name">One Property Australia</div>
      </div>
    </div>
  </div>
);

const WhySection: React.FC = () => (
  <section className="rw-why">
    <div className="rw-why-header">
      <h2>Built for High-Income Earners.<br />Not the Average Investor.</h2>
      <p>
        Generic property advice is designed for median incomes. Our strategies are engineered
        around your tax position, your timeline, and the retirement standard you've spent your
        career building toward.
      </p>
    </div>
    <div className="rw-why-grid">
      <div className="rw-why-card">
        <div className="rw-why-card-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="1.8">
            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
        </div>
        <h4>Solar Value-Add Strategy</h4>
        <p>Lift rental yield and capital value simultaneously — while capturing the depreciation deductions your income bracket makes extraordinarily powerful.</p>
      </div>
      <div className="rw-why-card">
        <div className="rw-why-card-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="1.8">
            <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <h4>Structural Tax Minimisation</h4>
        <p>At $200k+, your tax bill is your biggest wealth leak. Property is one of the few legal tools that lets you grow wealth while reducing what you hand to the ATO — every year.</p>
      </div>
      <div className="rw-why-card">
        <div className="rw-why-card-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="1.8">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <h4>Accountable Strategy, Not Advice</h4>
        <p>We don't hand you a report and disappear. You get a structured, long-term plan with ongoing reviews — so every decision is made with confidence, not guesswork.</p>
      </div>
      <div className="rw-why-card">
        <div className="rw-why-card-icon" style={{ background: 'rgba(0,196,180,0.12)' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00c4b4" strokeWidth="1.8">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
        </div>
        <h4>Retirement Wealth Clarity</h4>
        <p>Know exactly how your portfolio works alongside super — and what it will deliver at retirement. Not a rough estimate. A clear, numbers-backed runway.</p>
      </div>
    </div>
  </section>
);

export default RetirementWealth;
